import { useState } from "react";
import { BellRingIcon, PlusIcon, Trash2Icon } from "lucide-react";

import {
  useChannels,
  useCreateChannel,
  useCreateRule,
  useDeleteChannel,
  useDeleteRule,
  useRules,
  useUpdateChannel,
  useUpdateRule,
  type ChannelType,
  type NotificationChannel,
  type NotificationRule,
  type Severity,
} from "@/api/notifications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Tab = "channels" | "rules";

function ChannelDialog({
  open, onClose, editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: NotificationChannel | null;
}) {
  const create = useCreateChannel();
  const update = useUpdateChannel();
  const [name, setName] = useState(editing?.name ?? "");
  const [type, setType] = useState<ChannelType>(editing?.type ?? "email");
  const [emailsText, setEmailsText] = useState(
    Array.isArray((editing?.config as any)?.to)
      ? ((editing?.config as any).to as string[]).join(", ")
      : "",
  );
  const [webhookUrl, setWebhookUrl] = useState(
    (editing?.config as any)?.url ?? "",
  );
  const [active, setActive] = useState(editing?.is_active ?? true);

  if (!open) return null;
  const submit = async () => {
    const config: Record<string, unknown> = {};
    if (type === "email") {
      config.to = emailsText.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (type === "webhook") {
      config.url = webhookUrl;
    }
    if (editing) {
      await update.mutateAsync({
        id: editing.id,
        patch: { name, config, is_active: active },
      });
    } else {
      await create.mutateAsync({
        name, type, config, is_active: active,
      });
    }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">
            {editing ? "Edit channel" : "New channel"}
          </h3>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {!editing && (
            <div className="space-y-1">
              <Label>Type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as ChannelType)}
              >
                <option value="email">Email</option>
                <option value="webhook">Webhook</option>
                <option value="in_app">In-app feed</option>
              </select>
            </div>
          )}
          {type === "email" && (
            <div className="space-y-1">
              <Label>Recipients (comma-separated)</Label>
              <Input
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                placeholder="ops@example.com, audit@example.com"
              />
            </div>
          )}
          {type === "webhook" && (
            <div className="space-y-1">
              <Label>Webhook URL</Label>
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://hooks.example.com/sview360"
              />
            </div>
          )}
          {type === "in_app" && (
            <p className="text-xs text-muted-foreground">
              In-app channel fans out to all active tenant users by
              default; the bell badge updates on the next 30-second poll.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={!name} onClick={submit}>
              {editing ? "Save" : "Create"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ChannelsTab() {
  // TWO TABS, TWO QUERIES, AND THE EMPTY STATES ARE THE DANGEROUS KIND:
  // "No channels yet." and "No rules yet." both read as configuration facts.
  // On a 500 they say the tenant has no alert delivery configured — which is
  // the same sentence a correctly-empty tenant gets, on the surface that
  // decides whether anyone is told when a camera drops.
  const { data, isLoading, isError, error, refetch } = useChannels();
  const del = useDeleteChannel();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NotificationChannel | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <PlusIcon className="mr-2 h-4 w-4" />New channel
        </Button>
      </div>
      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="notification channels"
        onRetry={() => void refetch()}
        isEmpty={(data?.items ?? []).length === 0}
        emptyMessage="No channels yet."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Active</th>
                <th className="px-3 py-2 text-left">Target</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((c) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2"><Badge>{c.type}</Badge></td>
                  <td className="px-3 py-2">
                    <Badge variant={c.is_active ? "online" : "offline"}>
                      {c.is_active ? "on" : "off"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {c.type === "email"
                      ? ((c.config as any).to ?? []).join(", ")
                      : c.type === "webhook"
                        ? (c.config as any).url
                        : "all tenant users"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setEditing(c); setDialogOpen(true); }}
                      >Edit</Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={async () => {
                          if (!confirm(`Delete "${c.name}"?`)) return;
                          await del.mutateAsync(c.id);
                        }}
                      ><Trash2Icon className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>
      <ChannelDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}

function RuleDialog({
  open, onClose, editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: NotificationRule | null;
}) {
  const create = useCreateRule();
  const update = useUpdateRule();
  const { data: channels } = useChannels();
  const [name, setName] = useState(editing?.name ?? "");
  const [eventType, setEventType] = useState(editing?.event_type ?? "");
  const [minSeverity, setMinSeverity] = useState<Severity>(
    editing?.min_severity ?? "warning",
  );
  const [channelId, setChannelId] = useState(
    editing?.channel_id ?? (channels?.items?.[0]?.id ?? ""),
  );
  const [dedup, setDedup] = useState(editing?.dedup_window_seconds ?? 300);
  const [active, setActive] = useState(editing?.is_active ?? true);

  if (!open) return null;
  const submit = async () => {
    if (editing) {
      await update.mutateAsync({
        id: editing.id,
        patch: {
          name, event_type: eventType, min_severity: minSeverity,
          channel_id: channelId,
          dedup_window_seconds: dedup, is_active: active,
        },
      });
    } else {
      await create.mutateAsync({
        name, event_type: eventType, min_severity: minSeverity,
        channel_id: channelId,
        dedup_window_seconds: dedup, is_active: active,
      });
    }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">
            {editing ? "Edit rule" : "New rule"}
          </h3>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Event type</Label>
            <Input
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="camera_offline, recording_gap, …"
            />
          </div>
          <div className="space-y-1">
            <Label>Minimum severity</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={minSeverity}
              onChange={(e) => setMinSeverity(e.target.value as Severity)}
            >
              <option value="info">info</option>
              <option value="warning">warning</option>
              <option value="critical">critical</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>Channel</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
            >
              {(channels?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Dedup window (seconds)</Label>
            <Input
              type="number" min={0} max={86400}
              value={dedup} onChange={(e) => setDedup(Number(e.target.value))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              disabled={!name || !eventType || !channelId}
              onClick={submit}
            >
              {editing ? "Save" : "Create"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RulesTab() {
  const { data, isLoading, isError, error, refetch } = useRules();
  // `channels` only feeds a name lookup and the "create" button's disabled
  // state here; the rules query is what this tab renders, so it is the one
  // gated. A channels failure degrades a label, not a claim.
  const { data: channels } = useChannels();
  const del = useDeleteRule();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NotificationRule | null>(null);
  const chMap = new Map((channels?.items ?? []).map((c) => [c.id, c]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          disabled={(channels?.items ?? []).length === 0}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
        >
          <PlusIcon className="mr-2 h-4 w-4" />New rule
        </Button>
      </div>
      {(channels?.items ?? []).length === 0 && (
        <p className="text-sm text-muted-foreground">
          Create a channel first — rules dispatch to channels.
        </p>
      )}
      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="notification rules"
        onRetry={() => void refetch()}
        isEmpty={(data?.items ?? []).length === 0}
        emptyMessage="No rules yet."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Event type</th>
                <th className="px-3 py-2 text-left">Min severity</th>
                <th className="px-3 py-2 text-left">Channel</th>
                <th className="px-3 py-2 text-left">Dedup</th>
                <th className="px-3 py-2 text-left">Active</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.event_type}</td>
                  <td className="px-3 py-2"><Badge>{r.min_severity}</Badge></td>
                  <td className="px-3 py-2 text-xs">
                    {chMap.get(r.channel_id)?.name ?? r.channel_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.dedup_window_seconds}s</td>
                  <td className="px-3 py-2">
                    <Badge variant={r.is_active ? "online" : "offline"}>
                      {r.is_active ? "on" : "off"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setEditing(r); setDialogOpen(true); }}
                      >Edit</Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={async () => {
                          if (!confirm(`Delete "${r.name}"?`)) return;
                          await del.mutateAsync(r.id);
                        }}
                      ><Trash2Icon className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>
      <RuleDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}

export default function NotificationsSettings() {
  const [tab, setTab] = useState<Tab>("channels");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <BellRingIcon className="h-6 w-6" />
          Notifications
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage channels (email / webhook / in-app) and routing rules.
        </p>
      </div>
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab("channels")}
          className={cn(
            "px-3 py-2 text-sm",
            tab === "channels"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >Channels</button>
        <button
          onClick={() => setTab("rules")}
          className={cn(
            "px-3 py-2 text-sm",
            tab === "rules"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >Rules</button>
      </div>
      {tab === "channels" && <ChannelsTab />}
      {tab === "rules" && <RulesTab />}
    </div>
  );
}
